import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import './styles/section-viewer.scss';

export type RailTick = { id: string; label: string };

export type RailGroup = {
  key: string;
  label: string;
  anchorId: string | null;
  ticks: RailTick[];
};

export type DocumentStreamNoteRef = { key: string; label: string };

export type DocumentStreamFocusRequest = { key: string; seq: number };

type StreamContextValue = {
  anchorIdFor: (noteKey: string) => string;
  register: (noteKey: string, ticks: RailTick[]) => void;
  unregister: (noteKey: string) => void;
};

export type DocumentStreamNoteContext = {
  noteKey: string;
  register: (ticks: RailTick[]) => void;
  unregister: () => void;
};

const StreamContext = createContext<StreamContextValue | null>(null);
const StreamNoteContext = createContext<DocumentStreamNoteContext | null>(null);

const IN_VIEW_PX = 80;
const HOLD_MS = 1000;

export function useDocumentStreamNote(): DocumentStreamNoteContext | null {
  return useContext(StreamNoteContext);
}

export const NoteHead: React.FC<{
  title?: string | null;
  meta?: string | null;
  sticky?: boolean;
}> = ({ title, meta, sticky = false }) => (
  <div
    className={classes(
      'cmx-document-content-section-viewer__head',
      sticky && 'cmx-document-content-section-viewer__head--sticky'
    )}
  >
    <span className="cmx-document-content-section-viewer__title">
      {title ?? ''}
    </span>
    {meta && (
      <span className="cmx-document-content-section-viewer__meta">{meta}</span>
    )}
  </div>
);

export const DocumentStreamNote: React.FC<{
  noteKey: string;
  children: React.ReactNode;
}> = ({ noteKey, children }) => {
  const stream = useContext(StreamContext);
  const note = useMemo(
    () =>
      stream && {
        noteKey,
        register: (ticks: RailTick[]) => stream.register(noteKey, ticks),
        unregister: () => stream.unregister(noteKey)
      },
    [stream, noteKey]
  );
  return (
    <section
      id={stream?.anchorIdFor(noteKey)}
      className="document-stream__note"
      data-note-key={noteKey}
    >
      <StreamNoteContext.Provider value={note}>
        {children}
      </StreamNoteContext.Provider>
    </section>
  );
};

export const StreamTicks: React.FC<{ ticks: RailTick[] }> = ({ ticks }) => {
  const note = useDocumentStreamNote();
  const tickKey = ticks
    .map((tick) => `${tick.id}\u0000${tick.label}`)
    .join('|');
  const latest = useRef(ticks);
  latest.current = ticks;

  useEffect(() => {
    note?.register(latest.current);
  }, [note, tickKey]);

  useEffect(() => () => note?.unregister(), [note]);

  return null;
};

export type DocumentStreamProps = {
  notes: DocumentStreamNoteRef[];
  focusRequest?: DocumentStreamFocusRequest;
  onNoteInView?: (noteKey: string) => void;
  children: React.ReactNode;
};

export const DocumentStream: React.FC<DocumentStreamProps> = ({
  notes,
  focusRequest,
  onNoteInView,
  children
}) => {
  const idPrefix = useId();
  const [registry, setRegistry] = useState<Record<string, RailTick[]>>({});

  const anchorIdFor = useCallback(
    (noteKey: string) => `${idPrefix}-note-${noteKey}`,
    [idPrefix]
  );

  const register = useCallback((noteKey: string, ticks: RailTick[]) => {
    setRegistry((previous) =>
      sameTicks(previous[noteKey], ticks)
        ? previous
        : { ...previous, [noteKey]: ticks }
    );
  }, []);

  const unregister = useCallback((noteKey: string) => {
    setRegistry((previous) => {
      if (!(noteKey in previous)) {
        return previous;
      }
      const next = { ...previous };
      delete next[noteKey];
      return next;
    });
  }, []);

  const context = useMemo(
    () => ({ anchorIdFor, register, unregister }),
    [anchorIdFor, register, unregister]
  );

  const groups: RailGroup[] = notes.map((note) => ({
    key: note.key,
    label: note.label,
    anchorId: anchorIdFor(note.key),
    ticks: registry[note.key] ?? []
  }));

  const focus = focusRequest
    ? { anchorId: anchorIdFor(focusRequest.key), seq: focusRequest.seq }
    : undefined;

  return (
    <StreamContext.Provider value={context}>
      <div className="cmx-document-content-section-viewer document-stream">
        <div className="cmx-document-content-section-viewer__frame">
          <SectionRail
            groups={groups}
            focus={focus}
            onGroupInView={onNoteInView}
          />
          <div className="cmx-document-content-section-viewer__content document-stream__content">
            {children}
            <div className="document-stream__end">
              End of documents · {notes.length}{' '}
              {notes.length === 1 ? 'note' : 'notes'}
            </div>
            <div
              className="cmx-document-content-section-viewer__end-spacer"
              aria-hidden
            />
          </div>
        </div>
      </div>
    </StreamContext.Provider>
  );
};

function sameTicks(a: RailTick[] | undefined, b: RailTick[]): boolean {
  return (
    a !== undefined &&
    a.length === b.length &&
    a.every((tick, i) => tick.id === b[i].id && tick.label === b[i].label)
  );
}

function scrollParent(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function groupTargets(group: RailGroup): RailTick[] {
  if (group.ticks.length > 0) {
    return group.ticks;
  }
  return group.anchorId ? [{ id: group.anchorId, label: '' }] : [];
}

function targetsOf(groups: RailGroup[]): { id: string; group: string }[] {
  return groups.flatMap((group) =>
    groupTargets(group).map((tick) => ({ id: tick.id, group: group.key }))
  );
}

function lastReached<T>(
  items: T[],
  idOf: (item: T) => string | null,
  paneTop: number,
  atBottom: boolean
): T | undefined {
  if (atBottom) {
    return items[items.length - 1];
  }
  return items.reduce<T | undefined>((found, item) => {
    const id = idOf(item);
    const node = id ? document.getElementById(id) : null;
    return node && node.getBoundingClientRect().top - paneTop <= IN_VIEW_PX
      ? item
      : found;
  }, items[0]);
}

function classes(...names: (string | false)[]): string {
  return names.filter(Boolean).join(' ');
}

export const SectionRail: React.FC<{
  groups: RailGroup[];
  focus?: { anchorId: string; seq: number };
  onGroupInView?: (key: string) => void;
}> = ({ groups, focus, onGroupInView }) => {
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const railRef = useRef<HTMLElement>(null);
  const heldUntil = useRef(0);
  const focusPending = useRef(false);
  const handledSeq = useRef<number | undefined>(undefined);
  const latest = useRef({ groups, onGroupInView });
  latest.current = { groups, onGroupInView };

  const targets = targetsOf(groups);
  const targetKey = targets.map((target) => target.id).join('|');
  const activeId = currentId ?? targets[0]?.id ?? null;
  const activeGroup =
    targets.find((target) => target.id === activeId)?.group ?? null;
  const grouped = groups.some((group) => group.label);

  useEffect(() => {
    let reportedGroup: string | null = null;
    const update = (event: Event) => {
      const scroller = scrollParent(railRef.current);
      if (!scroller || event.target !== scroller) {
        return;
      }
      const paneTop = scroller.getBoundingClientRect().top;
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
      const { groups: current, onGroupInView: report } = latest.current;
      // A programmatic scroll (tab click, rail jump) passes through every note on the way; none
      // of those is the coder's position, so neither the report nor the tick follows it.
      if (Date.now() < heldUntil.current) {
        return;
      }
      const group = lastReached(current, (g) => g.anchorId, paneTop, atBottom);
      if (group && group.key !== reportedGroup) {
        reportedGroup = group.key;
        report?.(group.key);
      }
      const target = lastReached(
        targetsOf(current),
        (t) => t.id,
        paneTop,
        atBottom
      );
      if (target) {
        setCurrentId(target.id);
      }
    };
    document.addEventListener('scroll', update, {
      capture: true,
      passive: true
    });
    return () =>
      document.removeEventListener('scroll', update, { capture: true });
  }, []);

  const focusSeq = focus?.seq;
  useEffect(() => {
    if (!focus) {
      return;
    }
    const fresh = focus.seq !== handledSeq.current;
    if (fresh) {
      handledSeq.current = focus.seq;
      focusPending.current = true;
      heldUntil.current = Date.now() + HOLD_MS;
    } else if (!focusPending.current || Date.now() >= heldUntil.current) {
      return;
    }
    document.getElementById(focus.anchorId)?.scrollIntoView({
      behavior: fresh ? 'smooth' : 'auto',
      block: 'start'
    });
    const group = groups.find((g) => g.anchorId === focus.anchorId);
    if (group) {
      setCurrentId(group.ticks[0]?.id ?? group.anchorId);
    }
  }, [focusSeq, targetKey]);

  const jump = (event: React.MouseEvent, id: string) => {
    event.preventDefault();
    focusPending.current = false;
    heldUntil.current = Date.now() + HOLD_MS;
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setCurrentId(id);
  };

  return (
    <nav
      ref={railRef}
      className={classes(
        'cmx-document-content-section-viewer__rail',
        expanded && 'cmx-document-content-section-viewer__rail--expanded'
      )}
      aria-label="Jump to section"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setExpanded(false);
        }
      }}
    >
      {expanded ? (
        <div className="cmx-document-content-section-viewer__rail-drawer">
          <div className="cmx-document-content-section-viewer__rail-label">
            Jump to
          </div>
          {groups.map((group) => (
            <div
              key={group.key}
              className={classes(
                'cmx-document-content-section-viewer__rail-group',
                grouped &&
                  group.key === activeGroup &&
                  'cmx-document-content-section-viewer__rail-group--current'
              )}
            >
              {group.label &&
                (group.anchorId ? (
                  <a
                    href={`#${group.anchorId}`}
                    className="cmx-document-content-section-viewer__rail-group-label"
                    onClick={(event) => jump(event, group.anchorId as string)}
                  >
                    {group.label}
                  </a>
                ) : (
                  <div className="cmx-document-content-section-viewer__rail-group-label">
                    {group.label}
                  </div>
                ))}
              {group.ticks.map((tick) => (
                <a
                  key={tick.id}
                  href={`#${tick.id}`}
                  aria-current={tick.id === activeId ? 'location' : undefined}
                  className={classes(
                    'cmx-document-content-section-viewer__rail-link',
                    tick.id === activeId &&
                      'cmx-document-content-section-viewer__rail-link--current'
                  )}
                  onClick={(event) => jump(event, tick.id)}
                >
                  {tick.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="cmx-document-content-section-viewer__rail-ticks">
          {groups.map((group, index) => (
            <React.Fragment key={group.key}>
              {index > 0 && grouped && (
                <span
                  className="cmx-document-content-section-viewer__rail-sep"
                  aria-hidden
                />
              )}
              {groupTargets(group).map((tick) => {
                const title =
                  [group.label, tick.label].filter(Boolean).join(' · ') ||
                  group.label;
                return (
                  <a
                    key={tick.id}
                    href={`#${tick.id}`}
                    title={title}
                    aria-label={title}
                    aria-current={tick.id === activeId ? 'location' : undefined}
                    className={classes(
                      'cmx-document-content-section-viewer__tick',
                      tick.id === activeId &&
                        'cmx-document-content-section-viewer__tick--current'
                    )}
                    onClick={(event) => jump(event, tick.id)}
                  >
                    <span className="cmx-document-content-section-viewer__tick-dot" />
                  </a>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
    </nav>
  );
};
